import {
  LoginInfo,
  Selectors,
  getState,
  widgetDispatch,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {
  all,
  call,
  delay,
  put,
  select,
  takeLatest,
} from '@redux-saga/core/effects';
import {PayloadAction} from '@reduxjs/toolkit';
import {AxiosError, AxiosResponse} from 'axios';
import {DateTime} from 'luxon';
import {Platform} from 'react-native';

import {
  OCRMReward,
  Offer,
  OfferRequestStatus,
  OfferSource,
  OfferType,
  OffersResponse,
  SaveOfferPrefResponse,
  TDSOffer,
} from 'models/Offer';
import {
  OfferFiltersDestination,
  OfferFiltersProperty,
} from 'models/OfferFilters';
import {
  GetOffersConfig,
  GetOffersOpts,
  SaveOfferPrefConfig,
  SaveOfferPrefOpts,
  acceptingOCRMOffer,
  getOffers,
  saveOfferPref,
} from 'services/api/offers/offers';
import {AuthActions} from 'store/auth/auth.reducer';
import {LogoutMsg} from 'store/auth/auth.reducer';
import {isWebViewSelector} from 'store/content';
import {createDestinationsSelector} from 'store/properties/properties.selectors';
import {getRouteParams, setRouteParams} from 'utils/navigation';
import {tdsUrlPath} from 'utils/string';

import {OffersActions, SelectedFilters} from './offers.reducer';
import {
  offersLastQueryTimeSelector,
  offersListSelector,
  offersSelector,
} from './offers.selectors';
import {getAuthTokenSaga} from 'utils/getAuthTokenSaga';

export const OFFERS_REFRESH_INTERVAL_MIN = 5;

export function* handleGetOffers(action: PayloadAction<GetOffersOpts>) {
  const lastQueryTimes = yield select(offersLastQueryTimeSelector);

  /**
   * if it's right after login, we'll refetch ocrm offers in 5 mins
   */
  if (!lastQueryTimes.ocrm && !lastQueryTimes.all) {
    yield put(OffersActions.setDomMarket());
    yield put(OffersActions.refreshOcrmOffers());
  }

  const config = {
    data: {...action.payload},
  } as GetOffersConfig;

  /**
   * If no source was provided,
   * use our interval times to determine which request type we should make
   */
  if (!action.payload.source) {
    config.data.source = OfferSource.all;
  }

  /**
   * Configure requestor and token for API correctly
   * NOTE: Should this moved into the base
   */
  // let sectoken, oauthtoken;
  const token = yield call(getAuthTokenSaga);

  if (Platform.OS === 'web') {
    config.data.header = {sectoken: token};
  } else {
    // Native request
    config.data.header = {oauthtoken: token};
  }

  if (!token) {
    yield put(OffersActions.clearOffers());
    return;
  }

  try {
    const response = yield call(getOffers, config);
    const {data} = response as AxiosResponse<OffersResponse>;
    const payload: OffersResponse = {...data, source: config.data.source};

    /**
     * See if the response from API returned an error.
     * If so yield and return
     */
    if (data.header.status === OfferRequestStatus.FAILURE) {
      const isWebView = yield select(isWebViewSelector);
      // if failure is due to no auth, log user out
      if (
        data.header?.errors?.some(err => err.code === 'WCS0001' && !isWebView)
      ) {
        yield call(
          widgetDispatch,
          AuthActions.logout({msg: LogoutMsg.TIMEOUT})
        );
        return;
      }
      yield put(OffersActions.getOffersFailure(payload));
      return;
    }

    // Success, return the request data to the offers success action.
    if (
      lastQueryTimes.all &&
      lastQueryTimes.ocrm &&
      config.data.source === OfferSource.all
    ) {
      yield all([
        put(OffersActions.saveOffers(payload)),
        call(handleRemoveOldOffers, payload.offers),
      ]);
      return;
    }

    yield put(OffersActions.getOffersSuccess(payload));
  } catch (err) {
    if (typeof err === 'object') {
      const error = err as AxiosError;
      /**
       * We can have two types of axios failures.
       * 1. It is a response failure from the API
       * 2. It is an internal/config axios error (see "isAxiosError" property in AxiosError interface)
       * as well as https://axios-http.com/docs/handling_errors
       *
       * If it fails to yield the response from the API (the former), then we will yield back a generic
       * internal app failure (the payload below).
       */
      if (error?.response) {
        const axiosFailurePayload: OffersResponse = {
          source: action.payload.source,
          header: {
            status: OfferRequestStatus.FAILURE,
            errors: [{code: error?.request?.status, message: error?.message}],
          },
          offers: [],
        };

        yield put(OffersActions.getOffersFailure(axiosFailurePayload));
        return;
      }
    }
    /**
     * Something seriously went wrong if you see this.
     */
    const payload: OffersResponse = {
      source: action.payload.source,
      header: {
        status: OfferRequestStatus.FAILURE,
        errors: [
          {
            code: 'SAGAS_FAILURE',
            message: 'Internal App Failure',
          },
        ],
      },
      offers: [],
    };

    yield put(OffersActions.getOffersFailure(payload));
  }
}

export function* handleSaveOfferPref(action: PayloadAction<SaveOfferPrefOpts>) {
  yield put(OffersActions.saveOfferPrefStatus(action.payload.offerid));
  const config = {
    data: {...action.payload},
  } as SaveOfferPrefConfig;

  const token = yield call(getAuthTokenSaga);
  if (Platform.OS === 'web') {
    config.data.header = {sectoken: token};
  } else {
    config.data.header = {oauthtoken: token};
  }

  try {
    const response = yield call(saveOfferPref, config);
    const {data} = response as AxiosResponse<SaveOfferPrefResponse>;

    if (data.header.status === OfferRequestStatus.FAILURE) {
      yield put(OffersActions.saveOfferPrefFailure(data));
      return;
    }
  } catch (err) {
    if (typeof err === 'object') {
      const error = err as AxiosError;

      if (error?.response) {
        const axiosFailurePayload: SaveOfferPrefResponse = {
          header: {
            status: OfferRequestStatus.FAILURE,
            errors: [{code: error?.request?.status, message: error?.message}],
          },
        };

        yield put(OffersActions.saveOfferPrefFailure(axiosFailurePayload));
        return;
      }
    }

    const payload: SaveOfferPrefResponse = {
      header: {
        status: OfferRequestStatus.FAILURE,
        errors: [
          {
            code: 'SAGAS_FAILURE',
            message: 'Internal App Failure',
          },
        ],
      },
    };

    yield put(OffersActions.saveOfferPrefFailure(payload));
  } finally {
    yield put(OffersActions.saveOfferPrefStatus(action.payload.offerid));
  }
}

export function* handleAcceptOCRMOffer(action: PayloadAction<OCRMReward>) {
  const ocrmAcceptUrl = action.payload.links.link.find(
    link => link.rel === 'accept'
  )?.href;

  if (!ocrmAcceptUrl) {
    return;
  }

  const token = yield call(getState, Selectors.sectokenSelector);

  const config = {
    url: tdsUrlPath(ocrmAcceptUrl),
    headers: {
      Authorization: `${token}`,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  };

  try {
    const response = yield call(acceptingOCRMOffer, config);
    const {data} = response as AxiosResponse<{Offer?: TDSOffer}>;

    if (response.status === 200 && data.Offer) {
      yield put(OffersActions.acceptOCRMOfferSuccess(data.Offer));
      yield put(OffersActions.acceptOfferSuccess(undefined));
      return;
    }

    throw new Error('Unexpected error occurred when accepting OCRM offer.');
  } catch (error: any) {
    yield put(OffersActions.acceptOCRMOfferFailure(error?.message));
  }
}

export function* handleCheckOfferDeepLink(): Generator<unknown, void, any> {
  try {
    const search = yield call(() =>
      Platform.OS === 'web' ? window.location.search : getRouteParams()
    );
    const deepLinkedOffer = new URLSearchParams(search);
    const {offerid} = Object.fromEntries(deepLinkedOffer);

    if (!offerid) {
      return;
    }
    const offers: Offer[] = yield select(offersListSelector);
    const foundOffer = offers.find(offer => offer.ruleId === offerid);

    if (foundOffer) {
      yield put(OffersActions.setSelectedOfferId(foundOffer.id));
    }
  } catch (error) {}
}

export function* getDefaultOfferTabSaga() {
  try {
    const defaultTab = yield call(() => {
      return new URLSearchParams(window.location.search).get('dot');
    });

    if (defaultTab === 'AVAILABLE' || defaultTab === 'FAVORITES') {
      yield put(OffersActions.setActiveOfferTab(defaultTab));
    }
  } catch (err) {}
}

export function* handleAutoRefreshInterval(
  action?: PayloadAction<OffersResponse | undefined>
) {
  const shouldCancel = action?.type === OffersActions.pauseOffers.type;
  if (shouldCancel) {
    /**
     * Effectively cancel auto-refresh.
     * Will start back up when we re-gain focus on offers screen.
     */
    return;
  }

  /**
   * Include 1 second to avoid race between when when the lastQueryTime is
   * written in reducer and when this handler is fired by redux-saga.
   *
   * Without it, there's a chance this delay will finish before the actual
   * lastQueryTimes have elapsed enough time, and we will cancel below.
   */
  yield delay(1000 * 60 * OFFERS_REFRESH_INTERVAL_MIN + 1000);

  const config: GetOffersOpts = {
    source: OfferSource.all,
  };

  yield put(OffersActions.getOffers(config));
}

export function* handleFiltersDeepLink(): Generator<unknown, void, any> {
  try {
    const search = yield call(() =>
      Platform.OS === 'web' ? window.location.search : getRouteParams()
    );
    const deepLinkedFilters = new URLSearchParams(search);

    // https://stackoverflow.com/questions/8648892/how-to-convert-url-parameters-to-a-javascript-object

    const {destination, properties, offerTypes, startDate, endDate} =
      Object.fromEntries(deepLinkedFilters);
    if (!destination && !properties && !offerTypes && !startDate && !endDate) {
      return;
    }

    let preSelectedFilters: SelectedFilters = {
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
      type: offerTypes?.split(',').map(
        t =>
          t
            .split(' ')
            .map(ts => ts.charAt(0).toUpperCase() + ts.slice(1))
            .join(' ') as OfferType
      ),
      destination: undefined,
      properties: [],
    };

    if (destination || properties) {
      const destinations: OfferFiltersDestination[] = yield select(
        createDestinationsSelector
      );
      const destinationObject: OfferFiltersDestination | undefined = destination
        ? destinations.find(d => d.id === destination)
        : properties
          ? destinations.find(d =>
              d.properties.some(p => p.id === properties.split(',')[0])
            )
          : undefined;

      preSelectedFilters = {
        ...preSelectedFilters,
        destination: destinationObject,
        properties:
          properties && destinationObject
            ? properties
                .split(',')
                .map(
                  (p: string) =>
                    destinationObject.properties.find(
                      dp => dp.id === p
                    ) as OfferFiltersProperty
                )
            : [],
      };
    }
    yield put(OffersActions.setSelectedFilters(preSelectedFilters));
    yield call(setRouteParams, {
      destination: undefined,
      properties: undefined,
      offerTypes: undefined,
      startDate: undefined,
      endDate: undefined,
    });
  } catch (e) {}
}

export function* handleRemoveOldOffers(offers: Offer[]) {
  const oldOffers = yield select(offersSelector);
  const oldOffersList: Offer[] = Object.values(oldOffers);

  const refreshedOffers = oldOffersList.reduce<Offer[]>(
    (_offers, currentOffer: Offer) => {
      const endDateTime = DateTime.fromISO(currentOffer.end).toLocal();
      const now = DateTime.local();

      if (
        offers.some(offer => offer.id === currentOffer.id) &&
        endDateTime > now
      ) {
        return [..._offers, currentOffer];
      }
      return _offers;
    },
    []
  );
  if (refreshedOffers.length < oldOffersList.length) {
    yield put(OffersActions.removeExpiredOffers(refreshedOffers));
  }
}

export function* selectDominantDestination(): Generator<unknown, void, any> {
  try {
    const search = yield call(() => window.location.search);
    const deepLinkedFilters = new URLSearchParams(search);
    const {destination, properties, offerTypes, startDate, endDate} =
      Object.fromEntries(deepLinkedFilters);
    /**
     * If there are deep linked filters, we won't set the dominant property
     */
    if (destination || properties || offerTypes || (startDate && endDate)) {
      return;
    }

    const loginInfo = yield call(
      getState,
      Selectors.loginInfoSelector
    ) as LoginInfo;

    const propcode = loginInfo?.propcode || '';

    const destinations: OfferFiltersDestination[] = yield select(
      createDestinationsSelector
    );
    const domDestination =
      destinations.find(d =>
        d.properties.some(p => p.id === propcode?.toLowerCase())
      ) ?? destinations.find(d => d.id === 'lvm'); // if no valid destination, we'll default to Vegas
    yield put(
      OffersActions.setSelectedFilters({
        startDate: undefined,
        endDate: undefined,
        type: undefined,
        destination: domDestination,
        properties: [],
      })
    );
    yield put(OffersActions.showDomMarketBanner(true));
  } catch (e) {}
}

export default function* offersSagas() {
  yield all([
    takeLatest(OffersActions.getOffers, handleGetOffers),
    takeLatest(
      [
        OffersActions.pauseOffers,
        OffersActions.getOffersSuccess,
        OffersActions.getOffersFailure,
        OffersActions.saveOffers,
      ], // Allows any of the actions above in the list to cancel each-other.
      handleAutoRefreshInterval
    ),
    takeLatest(OffersActions.saveOfferPref, handleSaveOfferPref),
    takeLatest(OffersActions.acceptOCRMOffer, handleAcceptOCRMOffer),
    takeLatest(OffersActions.getOffersSuccess, handleCheckOfferDeepLink),
    takeLatest(OffersActions.saveOffers, handleCheckOfferDeepLink),
    takeLatest(OffersActions.getOffersSuccess.type, handleFiltersDeepLink),
    takeLatest(OffersActions.saveOffers.type, handleFiltersDeepLink),
    takeLatest(OffersActions.checkDefaultOfferTab.type, getDefaultOfferTabSaga),
    takeLatest(OffersActions.setDomMarket.type, selectDominantDestination),
  ]);
}
